import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Play, Pause, Volume2, VolumeX, Maximize2, Loader2, RotateCcw, Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration, isPrivateMedia, type PlaybackScope } from "@/lib/media";
import { getVideoPlayback } from "@/lib/media-access.functions";

export type VideoSource = {
  /** Row id of the media record — used to request a short-lived playback link. */
  id: string;
  /** Public URL, or a `secure://` marker for privately stored video. */
  url: string | null;
  posterUrl?: string | null;
  durationSeconds?: number | null;
  scope?: PlaybackScope;
};

/**
 * Resolves the actual playable URL. Public files play straight away; privately
 * stored video asks the server for a short-lived signed link, which is only
 * issued when the parent record is visible to the viewer.
 */
export function useVideoSource(source: VideoSource, enabled = true) {
  const resolve = useServerFn(getVideoPlayback);
  const [url, setUrl] = useState<string | null>(isPrivateMedia(source.url) ? null : source.url ?? null);
  const [poster, setPoster] = useState<string | null>(source.posterUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isPrivateMedia(source.url)) { setUrl(source.url ?? null); return; }
    setLoading(true); setError(null);
    try {
      const res = await resolve({ data: { scope: source.scope ?? "post", id: source.id } });
      setUrl(res.url);
      if (!source.posterUrl && res.posterUrl) setPoster(res.posterUrl);
    } catch {
      setError("This video could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [resolve, source.id, source.posterUrl, source.scope, source.url]);

  useEffect(() => { if (enabled) void load(); }, [enabled, load]);

  return { url, poster, error, loading, retry: load };
}

const SPEEDS = [0.5, 1, 1.25, 1.5, 2];

/**
 * The single Xamseen video surface: used in the feed, report detail, lightbox
 * and project timelines. Poster-first, keyboard reachable, no autoplay with
 * sound, and it never buffers a whole file just to show a thumbnail.
 */
export function VideoPlayer({
  source,
  className,
  objectFit = "cover",
  autoPlayMuted = false,
  loop = false,
  eager = false,
  showSpeed = true,
}: {
  source: VideoSource;
  className?: string;
  objectFit?: "cover" | "contain";
  autoPlayMuted?: boolean;
  loop?: boolean;
  /** Resolve and attach the file immediately instead of on first play. */
  eager?: boolean;
  showSpeed?: boolean;
}) {
  const [armed, setArmed] = useState(eager || autoPlayMuted);
  const { url, poster, error, loading, retry } = useVideoSource(source, armed);
  const ref = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(autoPlayMuted);
  const [buffering, setBuffering] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(source.durationSeconds ?? 0);
  const [speed, setSpeed] = useState(1);
  const [failed, setFailed] = useState(false);

  // Muted preview autoplay only while genuinely on screen, and never when the
  // viewer has asked for reduced motion or is saving data.
  useEffect(() => {
    const el = ref.current;
    if (!el || !autoPlayMuted || !url) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const saveData = (navigator as any)?.connection?.saveData === true;
    if (reduce || saveData) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) el.play().catch(() => {});
        else el.pause();
      }
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, [autoPlayMuted, url]);

  const toggle = (e?: React.MouseEvent) => {
    e?.preventDefault(); e?.stopPropagation();
    if (!armed) { setArmed(true); return; }
    const el = ref.current; if (!el) return;
    if (el.paused) el.play().catch(() => {}); else el.pause();
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const el = ref.current; if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const cycleSpeed = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (ref.current) ref.current.playbackRate = next;
  };

  const onKey = (e: React.KeyboardEvent) => {
    const el = ref.current;
    if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(); }
    if (!el) return;
    if (e.key === "ArrowRight") el.currentTime = Math.min(el.duration || 0, el.currentTime + 5);
    if (e.key === "ArrowLeft") el.currentTime = Math.max(0, el.currentTime - 5);
    if (e.key.toLowerCase() === "m") { el.muted = !el.muted; setMuted(el.muted); }
  };

  const showOverlay = !playing || buffering || loading;

  return (
    <div
      className={cn("group relative h-full w-full overflow-hidden bg-black", className)}
      onClick={toggle}
      onKeyDown={onKey}
      role="button"
      tabIndex={0}
      aria-label={playing ? "Pause video" : "Play video"}
    >
      {poster && !playing ? (
        <img src={poster} alt="" className={cn("absolute inset-0 h-full w-full", objectFit === "cover" ? "object-cover" : "object-contain")} loading="lazy" decoding="async" />
      ) : null}

      {url ? (
        <video
          ref={ref}
          src={url}
          poster={poster ?? undefined}
          muted={muted}
          loop={loop}
          playsInline
          preload={eager ? "metadata" : "none"}
          className={cn("relative h-full w-full", objectFit === "cover" ? "object-cover" : "object-contain")}
          onPlay={() => { setPlaying(true); setFailed(false); }}
          onPause={() => setPlaying(false)}
          onWaiting={() => setBuffering(true)}
          onPlaying={() => setBuffering(false)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
          onError={() => setFailed(true)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : null}

      {(error || failed) && (
        <div className="absolute inset-0 grid place-items-center gap-2 bg-black/80 p-4 text-center">
          <p className="text-sm text-white/85">{error ?? "This video could not be played."}</p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFailed(false); void retry(); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      )}

      {showOverlay && !error && !failed && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center bg-black/20">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-black/60 text-white backdrop-blur">
            {loading || buffering ? <Loader2 className="h-6 w-6 animate-spin" /> : playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 fill-current" />}
          </span>
        </div>
      )}

      {url && !error && !failed && (
        <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-2.5 pb-2 pt-6 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          <button
            type="button"
            onClick={toggle}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/80"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 fill-current" />}
          </button>
          <div className="h-1 flex-1 cursor-pointer rounded-full bg-white/25" onClick={seek}>
            <div className="h-full rounded-full bg-white" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
          </div>
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-white/85">
            {formatDuration(progress)}{duration ? ` / ${formatDuration(duration)}` : ""}
          </span>
          {showSpeed && (
            <button
              type="button"
              onClick={cycleSpeed}
              className="hidden shrink-0 items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[11px] font-medium text-white backdrop-blur hover:bg-black/80 sm:inline-flex"
              aria-label="Playback speed"
            >
              <Gauge className="h-3.5 w-3.5" /> {speed}x
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); const el = ref.current; if (!el) return; el.muted = !el.muted; setMuted(el.muted); }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/80"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); ref.current?.requestFullscreen?.().catch(() => {}); }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/80"
            aria-label="Fullscreen"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
